import type { DateRange } from '@mui/x-date-pickers-pro/models';
import type { PickersShortcutsItem } from '@mui/x-date-pickers/PickersShortcuts';

import { DateRangePicker } from '@mui/x-date-pickers-pro';
import { format, subDays } from 'date-fns';
import { useSearchParams } from 'react-router';

import { FORMATS, getDefaultDateRange } from '#utils/dates.ts';

/**
 * Date ranges used in the date filter. The first date of every tuple is one
 * day less than the label because the second date is the current date, which
 * adds the extra day required to match the label.
 */
const shortcuts: PickersShortcutsItem<DateRange<Date>>[] = [
  {
    // Default
    label: 'Last 90 Days',
    getValue: getDefaultDateRange,
  },
  {
    label: 'Last 180 Days',
    getValue: () => [subDays(new Date(), 179), new Date()],
  },
  {
    label: 'Last 365 Days',
    getValue: () => [subDays(new Date(), 364), new Date()],
  },
  {
    label: 'Reset',
    getValue: getDefaultDateRange,
  },
];

export function DateRange(
  props: Pick<React.ComponentProps<typeof DateRangePicker>, 'defaultValue'>
) {
  const [, setSearchParams] = useSearchParams();

  return (
    <DateRangePicker
      localeText={{ cancelButtonLabel: 'Dismiss', okButtonLabel: 'Save' }}
      closeOnSelect={false}
      disableFuture
      defaultValue={props.defaultValue}
      label="Date Range"
      onAccept={([from, to]) => {
        // Ignore null values
        if (!from || !to) return;
        setSearchParams((searchParams) => {
          searchParams.set('from', format(from, FORMATS.MYSQL_DATE));
          searchParams.set('to', format(to, FORMATS.MYSQL_DATE));
          return searchParams;
        });
      }}
      slotProps={{
        actionBar: {
          actions: ['cancel', 'accept'],
        },
        shortcuts: {
          items: shortcuts,
        },
        textField: {
          fullWidth: true,
        },
      }}
    />
  );
}
